import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { ReactNode } from "react";
import { useTranslations } from "use-intl";

interface UnmatchedAcknowledgmentProps {
  checkboxId: string;
  acknowledged: boolean;
  onAcknowledgedChange: (checked: boolean) => void;
  /** The external-settlement date/SEK-amount fields — each caller passes its own
   * ExternalSettlementFields element (receivables never shows the SEK field here; payables and
   * receipts do), keeping that domain difference entirely in the caller. */
  children: ReactNode;
  noneOfTheseMatch: boolean;
  onBackToSuggestions: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  confirmDisabled: boolean;
}

/**
 * Case c (zero candidates, or "None of these match" was clicked — design spec Flows 3 & 4),
 * shared across every "match to transaction" modal (payables, receivables, receipts, issue
 * #498): a bank account is connected but nothing matched this specific payment — a single
 * fixed-copy checkbox must be ticked before Confirm is enabled (AC4/AC5). The checkbox's
 * wording is the design spec's fixed sentence (`consultantMatching.shared.acknowledgment`, see
 * docs/superpowers/specs/2026-08-02-merge-paid-externally-match-action-design.md, "UX
 * Requirements → Acknowledgment copy") in each language — never free text or a dropdown.
 */
export default function UnmatchedAcknowledgment({
  checkboxId,
  acknowledged,
  onAcknowledgedChange,
  children,
  noneOfTheseMatch,
  onBackToSuggestions,
  onCancel,
  onConfirm,
  confirmDisabled,
}: UnmatchedAcknowledgmentProps) {
  const t = useTranslations("consultantMatching.shared");
  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">{t("noMatchFound")}</div>
      <div className="flex items-start gap-2">
        <Checkbox
          id={checkboxId}
          checked={acknowledged}
          onCheckedChange={(checked) => onAcknowledgedChange(checked === true)}
          className="mt-0.5"
        />
        <Label htmlFor={checkboxId} className="text-sm font-normal leading-snug">
          {t("acknowledgment")}
        </Label>
      </div>
      {children}
      {noneOfTheseMatch && (
        <div className="text-center">
          <Button variant="link" size="sm" onClick={onBackToSuggestions}>
            {t("backToSuggestions")}
          </Button>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>{t("cancel")}</Button>
        <Button onClick={onConfirm} disabled={confirmDisabled}>
          {t("confirm")}
        </Button>
      </DialogFooter>
    </div>
  );
}
